import { useRef, useState } from 'react';

import {
  Bildschirmtastatur,
  TastaturKnopf,
  istTastaturEsc,
  useBildschirmtastatur,
} from './Bildschirmtastatur';
import { useScanSchutz } from '../scan-schutz';
import { useFokusfalleAmFenster } from './zahlung-v2/fokus';

/** Höchstlänge des Storno-Grunds (Belegtext `Storno <Nr>: <Grund>` bleibt ≤ 200 Zeichen). */
export const STORNO_GRUND_MAX = 150;

/**
 * BT2-SN/BT3-TQ-SN (R4): Storno-Grund als eigener Dialog statt `window.prompt`. Pflicht, getrimmt,
 * höchstens 150 Zeichen; „Abbrechen“ (Esc) setzt nichts fort, „Weiter zum Storno“ (Enter) nur
 * einmal. Der Auftrag (Originalbeleg, Zahlungsart) hält der Aufrufer beim Öffnen fest.
 */
export function StornoGrundDialog(props: {
  belegnummer: string;
  /** Vorbelegung (zuletzt eingegebener Grund). */
  start: string;
  onWeiter: (grund: string) => void;
  onAbbrechen: () => void;
}) {
  const [grund, setGrund] = useState(props.start);
  const [fehler, setFehler] = useState<string | null>(null);
  const gesendet = useRef(false);
  const [gesperrt, setGesperrt] = useState(false);
  const dialog = useRef<HTMLDivElement | null>(null);
  useFokusfalleAmFenster(dialog, true);
  const bt = useBildschirmtastatur({ standardZiel: 'grund' });
  // W-2: Ein Scan füllt den Grund nicht und bestätigt ihn nicht (Enter des Scanners). Der
  // Verkauf ist hier nicht eingehängt – derselbe Schutz wie in der Einrichtung.
  useScanSchutz(setFehler);

  function weiter(): void {
    if (gesendet.current) {
      return;
    }
    const g = grund.trim();
    if (g === '') {
      setFehler('Storno braucht einen Grund');
      return;
    }
    gesendet.current = true;
    setGesperrt(true);
    props.onWeiter(g.slice(0, STORNO_GRUND_MAX));
  }

  return (
    <div
      ref={dialog}
      className="zahlung-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="storno-grund-titel"
      tabIndex={-1}
      style={{ outline: 'none' }}
      onKeyDown={(e) => {
        // R5: erstes Esc bei offener Tastatur schließt nur sie
        if (e.key === 'Escape' && !istTastaturEsc(e)) {
          e.preventDefault();
          props.onAbbrechen();
        }
      }}
    >
      <div
        className={`karte dlg storno-grund-dialog${bt.tastatur === null ? '' : ' mit-tastatur'}`}
        style={{ textAlign: 'left' }}
      >
        <div className="dlg-kopf">
          <h2 id="storno-grund-titel">Storno-Grund</h2>
          <span className="dlg-meta">Beleg {props.belegnummer}</span>
        </div>
        <div className="dlg-inhalt">
          <div className="dlg-feld">
            <label htmlFor="storno-grund" className="dlg-feldname">
              Grund (Pflicht, steht am Stornobeleg) · {grund.length}/{STORNO_GRUND_MAX}
            </label>
            <div className="feld-mit-knopf">
              <input
                id="storno-grund"
                data-scansperre="Storno-Grund"
                value={grund}
                maxLength={STORNO_GRUND_MAX}
                autoFocus
                placeholder="z. B. Kunde hat storniert"
                onChange={(e) => {
                  setGrund(e.target.value);
                  setFehler(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    weiter();
                  }
                }}
                {...bt.feld('grund', {
                  name: 'Storno-Grund',
                  setzen: (v) => {
                    setGrund(v);
                    setFehler(null);
                  },
                })}
              />
              <TastaturKnopf
                steuerung={bt.knopf('grund')}
                klein
                beschriftung="Tastatur für den Grund"
              />
            </div>
          </div>
          {fehler !== null && (
            <p className="fehler" role="alert">
              {fehler}
            </p>
          )}
        </div>
        {bt.tastatur !== null && <Bildschirmtastatur steuerung={bt.tastatur} />}
        <div className="dlg-fuss">
          <button
            type="button"
            className="taste zweit"
            data-kuerzel="Esc"
            onClick={props.onAbbrechen}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="taste gefahr"
            data-kuerzel="Enter"
            disabled={gesperrt}
            onClick={weiter}
          >
            Weiter zum Storno
          </button>
        </div>
      </div>
    </div>
  );
}
